"use client";

import React from "react";
import { Loader, Zap, ArrowRight } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function MidnightRunnersConsumer() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div data-section-id="405" className="flex h-96 w-full items-center justify-center">
        <Loader className="text-primary h-10 w-10 animate-spin" />
      </div>;
  }
  if (error) {
    return <div className="flex h-96 w-full items-center justify-center text-destructive">
        Error loading experiences.
      </div>;
  }
  if (!products || products.length === 0) {
    return <div className="flex h-96 w-full items-center justify-center text-muted-foreground">
        No curated journeys found at this time.
      </div>;
  }
  return <Carousel opts={{
    align: "start",
    loop: true
  }} className="w-full">
      <CarouselContent className="-ml-4">
        {products.map((product, i) => <CarouselItem key={product.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
            <div className="group relative h-full">
              <Card className="h-full overflow-hidden border-border/20 bg-card/60 backdrop-blur-md transition-all duration-500 hover:border-secondary/50 hover:shadow-[0_0_40px_-10px_rgba(184,153,78,0.3)]">
                <CardContent className="p-0">
                  {}
                  <div className="relative aspect-[4/5] overflow-hidden bg-muted/10">
                    {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" /> : <div className="flex h-full w-full items-center justify-center bg-muted">
                        <span className="text-muted-foreground font-default">Boutique View</span>
                      </div>}
                    
                    {}
                    <div className="absolute top-4 left-4 z-10">
                      <Badge variant="secondary" className="bg-primary/90 text-primary-foreground border-primary/20 shadow-[0_4px_12px_rgba(38,107,100,0.4)] backdrop-blur-md font-mono text-[10px] tracking-widest uppercase">
                        <Zap className="mr-1 h-3 w-3 fill-current" />
                        Luxe Boutique
                      </Badge>
                    </div>

                    {}
                    <div className="absolute right-4 bottom-4 left-4 translate-y-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                      <AddToCartButton productId={product.id} variant="default" className="w-full" buttonClassName="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-mono font-bold tracking-widest shadow-lg border-none" text="BOOK EXPERIENCE" showQuantity={false} />
                    </div>
                  </div>

                  {}
                  <div className="p-6">
                    <Link to={product.link} className="group/link block">
                      <h3 className="font-default text-xl font-semibold tracking-tight text-foreground transition-colors group-hover/link:text-secondary line-clamp-1">
                        {product.title}
                      </h3>
                    </Link>
                    
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-mono">
                          Signature Curation
                        </span>
                        <span className="font-default text-2xl font-light text-secondary mt-1">
                          ${product.price?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </CarouselItem>)}
      </CarouselContent>

      {}
      <div className="mt-12 flex items-center justify-end gap-3 pr-4">
        <CarouselPrevious className="static translate-y-0 border-secondary/30 bg-background/80 text-secondary hover:border-secondary hover:text-secondary-foreground hover:bg-secondary transition-all duration-300" variant="outline" />
        <CarouselNext className="static translate-y-0 border-secondary/30 bg-background/80 text-secondary hover:border-secondary hover:text-secondary-foreground hover:bg-secondary transition-all duration-300" variant="outline" />
      </div>
    </Carousel>;
}
export default function MidnightRunnersCollection() {
  const wp_query_params = {
    per_page: 8,
    product_tag: [22],
    orderby: "date",
    order: "desc",
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="ps-traveler-care-team" className="dark bg-background text-foreground relative overflow-hidden py-32">
      {}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-[20%] -left-[10%] h-[600px] w-[600px] rounded-full bg-secondary/5 blur-[140px]" />
        <div className="absolute top-[40%] -right-[10%] h-[700px] w-[700px] rounded-full bg-primary/5 blur-[140px]" />
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div className="mb-16 flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-3xl">
            <Badge variant="outline" className="mb-6 border-secondary/40 text-secondary font-mono tracking-widest uppercase px-4 py-1">PS Travel co.</Badge>
            <h2 className="font-default text-5xl font-light tracking-tight text-white md:text-6xl lg:text-7xl">
              Signature <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary via-secondary/80 to-primary italic">Experiences</span>
            </h2>
            <p className="mt-6 text-xl text-muted-foreground max-w-2xl leading-relaxed font-sans">
              Don’t just travel, PS Travel. We curate journeys that transcend the map, blending 70 years of global heritage with immersive local culture.
            </p>
          </div>
          
          <Link to="#">
            <Button variant="link" className="text-secondary hover:text-secondary/80 p-0 h-auto text-lg group font-mono tracking-wider">
              EXPLORE ALL <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-2" />
            </Button>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 8,
        product_tag: [22],
        orderby: "date",
        order: "desc",
        status: ["publish"]
      }} className="w-full">
          <MidnightRunnersConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}